import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { ref, get, set } from 'firebase/database';
import { firestore, database } from '../firebaseConfig';
import { useActiveWorld } from '../hooks/useActiveWorld';
import useCustomFields from '../hooks/useCustomFields';
import './ApiStagingReview.css';

const NUMERIC_FIELDS = new Set(['lead_rating', 'lead_reviews', 'lead_stars', 'lead_instagram_followers', 'lead_instagram_posts']);

const ApiStagingReview = ({ onClose, onImportComplete }) => {
  const { userId } = useActiveWorld();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [customImportName, setCustomImportName] = useState('');
  
  // Erweiterte Features
  const [existingImportNames, setExistingImportNames] = useState([]);
  const [showImportNameDropdown, setShowImportNameDropdown] = useState(false);
  const duplicateMode = 'skip';
  
  // Field Mapping Feature
  const [fieldMapping, setFieldMapping] = useState({});
  const [availableSourceFields, setAvailableSourceFields] = useState([]);
  const [hasSavedMapping, setHasSavedMapping] = useState(false);
  const [autoImportEnabled, setAutoImportEnabled] = useState(false);
  const [savingAutoImport, setSavingAutoImport] = useState(false);
  
  const { customFields } = useCustomFields(userId);

  const loadSavedMapping = useCallback(async (apiKeyId, sourceFields) => {
    if (!userId || !apiKeyId) return null;
    try {
      const mappingRef = ref(database, `World/${userId}/settings/apiMappings/${apiKeyId}`);
      const snapshot = await get(mappingRef);
      if (snapshot.exists()) {
        const saved = snapshot.val();
        const validMapping = {};
        for (const [target, source] of Object.entries(saved)) {
          if (target.startsWith('_')) continue;
          if (Array.isArray(source)) {
            const valid = source.filter(s => sourceFields.has(s));
            if (valid.length === 1) validMapping[target] = valid[0];
            else if (valid.length > 1) validMapping[target] = valid;
          } else if (typeof source === 'string' && sourceFields.has(source)) {
            validMapping[target] = source;
          }
        }
        if (Object.keys(validMapping).length > 0) {
          return { mapping: validMapping, autoImport: saved._autoImport || false, importName: saved._importName || '' };
        }
      }
    } catch (e) { console.error('[ApiStagingReview] Error loading saved mapping:', e); }
    return null;
  }, [userId]);

  const saveMappingForKey = useCallback(async (apiKeyId, mapping, { autoImport = false, importName = '' } = {}) => {
    if (!userId || !apiKeyId) return;
    try {
      const mappingRef = ref(database, `World/${userId}/settings/apiMappings/${apiKeyId}`);
      await set(mappingRef, { ...mapping, _savedAt: new Date().toISOString(), _autoImport: autoImport, _importName: importName });
      console.log('[ApiStagingReview] Mapping saved for API key:', apiKeyId, 'autoImport:', autoImport);
    } catch (e) { console.error('[ApiStagingReview] Error saving mapping:', e); }
  }, [userId]);

  const targetFields = React.useMemo(() => {
    const base = [
      { key: 'lead_email', label: 'Email', icon: '✉️' },
      { key: 'business_name', label: 'Business Name', icon: '🏢' },
      { key: 'lead_description', label: 'Description (AI Context)', icon: '📝' },
      { key: 'lead_telephone', label: 'Telephone', icon: '📞' },
      { key: 'lead_website', label: 'Website', icon: '🌐' },
      { key: 'lead_address', label: 'Address', icon: '🏠' },
      { key: 'lead_city', label: 'City', icon: '📍' },
      { key: 'lead_postcode', label: 'Postcode', icon: '📮' },
      { key: 'lead_country', label: 'Country', icon: '🌍' },
      { key: 'lead_instagram', label: 'Instagram', icon: '📸' },
      { key: 'lead_linkedin', label: 'LinkedIn', icon: '💼' },
      { key: 'lead_tiktok', label: 'TikTok', icon: '🎵' },
      { key: 'lead_facebook', label: 'Facebook', icon: '👥' },
      { key: 'lead_rating', label: 'Total Rating', icon: '⭐' },
      { key: 'lead_reviews', label: 'Total Reviews', icon: '📊' },
      { key: 'lead_stars', label: 'Stars', icon: '🌟' },
      { key: 'lead_link', label: 'Link', icon: '🔗' },
      { key: 'lead_image', label: 'Image', icon: '🖼️' },
      { key: 'lead_mapsurl', label: 'Google Maps URL', icon: '📍' },
      { key: 'lead_instagram_id', label: 'Instagram ID', icon: '📸' },
      { key: 'lead_instagram_username', label: 'Instagram Username', icon: '📸' },
      { key: 'lead_instagram_followers', label: 'Instagram Followers', icon: '👥' },
      { key: 'lead_instagram_posts', label: 'Instagram Posts', icon: '📷' },
      { key: 'lead_instagram_category', label: 'Instagram Category', icon: '🏷️' },
      { key: 'lead_category', label: 'Category', icon: '🏷️' },
      { key: 'lead_title', label: 'Job Title', icon: '💼' },
      { key: 'dm_name', label: 'Decision Maker Name', icon: '👤' },
      { key: 'dm_role', label: 'Decision Maker Position', icon: '👤' },
      { key: 'dm_email', label: 'Decision Maker Email', icon: '✉️' },
      { key: 'dm_phone', label: 'Decision Maker Phone', icon: '📞' },
    ];
    customFields.forEach((cf) => {
      base.push({ key: cf.key, label: cf.label, icon: '📋' });
    });
    return base;
  }, [customFields]);

  const functions = getFunctions(undefined, 'europe-west1');

  useEffect(() => {
    loadBatches();
    loadExistingImportNames();
  }, [userId]);
  
  // Bestehende Import-Namen aus der DB laden
  const loadExistingImportNames = async () => {
    if (!userId) return;
    
    try {
      const importNamesRef = collection(firestore, `World/${userId}/import_names`);
      const q = query(importNamesRef, orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      
      const names = snapshot.docs.map(doc => doc.data().name || doc.id);
      setExistingImportNames(names);
    } catch (err) {
      console.error('Error loading import names:', err);
    }
  };

  const loadBatches = async () => {
    if (!userId) {
      console.log('[ApiStagingReview] No userId, skipping load');
      return;
    }
    
    console.log('[ApiStagingReview] Loading batches for userId:', userId);
    setLoading(true);
    setError(null);
    
    try {
      const getBatches = httpsCallable(functions, 'getApiStagingBatches');
      console.log('[ApiStagingReview] Calling getApiStagingBatches...');
      const result = await getBatches({ userId });
      
      console.log('[ApiStagingReview] Result:', result.data);
      
      if (result.data.success) {
        const allBatches = result.data.batches || [];
        const pendingBatches = allBatches.filter(b => b.status === 'pending');
        setBatches(pendingBatches);
        
        if (pendingBatches.length > 0 && !selectedBatch) {
          const latest = pendingBatches[pendingBatches.length - 1];
          setSelectedBatch(latest);
          setCustomImportName(latest.importName || '');
          loadContacts(latest.id, latest);
        }
      }
    } catch (err) {
      console.error('[ApiStagingReview] Error loading staging batches:', err);
      setError('Failed to load staging batches: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadContacts = async (batchId, batchObj = null) => {
    setLoadingContacts(true);
    setError(null);
    
    try {
      const getContacts = httpsCallable(functions, 'getApiStagingContacts');
      const result = await getContacts({ userId, batchId });
      
      if (result.data.success) {
        setContacts(result.data.contacts);
        
        // Extrahiere verfügbare Felder aus den Kontakten
        if (result.data.contacts.length > 0) {
          const allFields = new Set();
          result.data.contacts.slice(0, 10).forEach(contact => {
            Object.keys(contact).forEach(key => {
              if (key !== 'id' && !key.startsWith('_')) {
                allFields.add(key);
              }
            });
          });
          setAvailableSourceFields(Array.from(allFields).sort());
          
          // Try to load saved mapping for this API key first
          const batchApiKeyId = batchObj?.apiKeyId || selectedBatch?.apiKeyId;
          const savedMapping = batchApiKeyId ? await loadSavedMapping(batchApiKeyId, allFields) : null;

          if (savedMapping && Object.keys(savedMapping.mapping).length > 0) {
            setFieldMapping(savedMapping.mapping);
            setHasSavedMapping(true);
            setAutoImportEnabled(savedMapping.autoImport || false);
            if (savedMapping.importName) setCustomImportName(savedMapping.importName);
            console.log('[ApiStagingReview] Loaded saved mapping for key:', batchApiKeyId, 'autoImport:', savedMapping.autoImport);
          } else {
            setHasSavedMapping(false);
            setAutoImportEnabled(false);

            // Auto-Mapping vorschlagen
            const autoMapping = {};
            const autoMappingRules = {
              lead_email: ['email', 'Email', 'EMAIL', 'e-mail', 'mail', 'lead_email'],
              business_name: ['company', 'Company', 'business', 'name', 'business_name', 'companyName'],
              lead_description: ['description', 'Description', 'notes', 'lead_description', 'about'],
              lead_telephone: ['phone', 'Phone', 'telefon', 'tel', 'mobile', 'lead_phone', 'lead_telephone', 'telephone'],
              lead_website: ['website', 'Website', 'url', 'web', 'homepage', 'lead_website'],
              lead_address: ['address', 'Address', 'street', 'adresse', 'lead_address', 'strasse'],
              lead_city: ['city', 'City', 'stadt', 'ort', 'location', 'lead_city'],
              lead_postcode: ['zip', 'ZIP', 'postal', 'plz', 'zipcode', 'lead_zip', 'lead_postcode', 'postcode'],
              lead_country: ['country', 'Country', 'land', 'lead_country'],
              lead_instagram: ['instagram', 'Instagram', 'ig', 'insta', 'lead_instagram'],
              lead_linkedin: ['linkedin', 'LinkedIn', 'lead_linkedin'],
              lead_tiktok: ['tiktok', 'TikTok', 'tik_tok', 'lead_tiktok'],
              lead_facebook: ['facebook', 'Facebook', 'fb', 'lead_facebook'],
              lead_rating: ['rating', 'Rating', 'lead_rating', 'total_rating', 'bewertung'],
              lead_reviews: ['reviews', 'Reviews', 'lead_reviews', 'total_reviews', 'review_count'],
              lead_stars: ['stars', 'Stars', 'lead_stars', 'sterne'],
              lead_link: ['link', 'Link', 'lead_link', 'source_url'],
              lead_image: ['image', 'Image', 'photo', 'logo', 'lead_image', 'bild'],
              lead_mapsurl: ['maps_url', 'mapsurl', 'google_maps', 'lead_mapsurl', 'maps'],
              lead_instagram_followers: ['followers', 'Followers', 'lead_instagram_followers', 'instagram_followers'],
              lead_instagram_username: ['instagram_username', 'lead_instagram_username', 'ig_username'],
              lead_instagram_category: ['instagram_category', 'lead_instagram_category', 'ig_category'],
              lead_category: ['category', 'Category', 'lead_category', 'branche', 'kategorie'],
              lead_title: ['title', 'job_title', 'jobTitle', 'lead_title', 'titel'],
              dm_name: ['firstName', 'firstname', 'first_name', 'vorname', 'lead_firstname', 'contact_name', 'ansprechpartner', 'dm_name'],
              dm_role: ['position', 'role', 'dm_role'],
              dm_email: ['dm_email', 'contact_email', 'decision_maker_email'],
              dm_phone: ['dm_phone', 'contact_phone', 'decision_maker_phone'],
            };

            for (const [targetField, possibleSources] of Object.entries(autoMappingRules)) {
              for (const source of possibleSources) {
                if (allFields.has(source)) {
                  autoMapping[targetField] = source;
                  break;
                }
              }
            }
            setFieldMapping(autoMapping);
          }
        }
      }
    } catch (err) {
      console.error('Error loading contacts:', err);
      setError('Failed to load contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const selectBatch = async (batch) => {
    setSelectedBatch(batch);
    setCustomImportName(batch.importName || '');
    await loadContacts(batch.id, batch);
  };

  const handleImport = async () => {
    if (!selectedBatch) return;
    
    // Validierung: Import-Name darf nicht leer sein
    const finalImportName = customImportName.trim();
    if (!finalImportName) {
      setError('Please enter an import name');
      return;
    }
    
    setImporting(true);
    setError(null);
    
    try {
      const importBatch = httpsCallable(functions, 'importApiStagingBatch');
      const result = await importBatch({
        userId,
        batchId: selectedBatch.id,
        importName: finalImportName,
        duplicateMode: duplicateMode, // 'skip', 'merge', 'update'
        fieldMapping: fieldMapping // Field Mapping für die Zuordnung
      });
      
      if (result.data.success) {
        if (selectedBatch.apiKeyId) {
          const mappingToSave = {};
          for (const [k, v] of Object.entries(fieldMapping)) {
            if (Array.isArray(v) ? v.length > 0 : !!v) mappingToSave[k] = v;
          }
          if (Object.keys(mappingToSave).length > 0) {
            await saveMappingForKey(selectedBatch.apiKeyId, mappingToSave, {
              autoImport: autoImportEnabled,
              importName: finalImportName
            });
          }
        }

        setBatches(prev => prev.filter(b => b.id !== selectedBatch.id));
        setSelectedBatch(null);
        setContacts([]);
        setCustomImportName('');
        setFieldMapping({});
        setHasSavedMapping(false);
        
        // Neue Import-Namen Liste aktualisieren
        if (!existingImportNames.includes(finalImportName)) {
          setExistingImportNames(prev => [...prev, finalImportName].sort());
        }
        
        if (onImportComplete) {
          onImportComplete(result.data);
        }
      }
    } catch (err) {
      console.error('Error importing batch:', err);
      setError('Failed to import batch');
    } finally {
      setImporting(false);
    }
  };
  
  // Import-Name aus Dropdown auswählen
  const selectImportName = (name) => {
    setCustomImportName(name);
    setShowImportNameDropdown(false);
  };
  
  // Gefilterte Import-Namen basierend auf Eingabe
  const filteredImportNames = existingImportNames.filter(name => 
    name.toLowerCase().includes(customImportName.toLowerCase())
  );

  const handleDelete = async () => {
    if (!selectedBatch) return;
    
    if (!window.confirm(`Are you sure you want to delete this batch with ${selectedBatch.contactCount} contacts? This cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    setError(null);
    
    try {
      const deleteBatch = httpsCallable(functions, 'deleteApiStagingBatch');
      const result = await deleteBatch({ userId, batchId: selectedBatch.id });
      
      if (result.data.success) {
        setBatches(prev => prev.filter(b => b.id !== selectedBatch.id));
        setSelectedBatch(null);
        setContacts([]);
        setCustomImportName('');
      }
    } catch (err) {
      console.error('Error deleting batch:', err);
      setError('Failed to delete batch');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Just now';
    try {
      // Handle Firestore Timestamp objects
      let date;
      if (dateStr?.toDate) {
        date = dateStr.toDate();
      } else if (dateStr?._seconds) {
        date = new Date(dateStr._seconds * 1000);
      } else if (dateStr?.seconds) {
        date = new Date(dateStr.seconds * 1000);
      } else {
        date = new Date(dateStr);
      }
      
      if (isNaN(date.getTime())) return 'Just now';
      
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (err) {
      return 'Just now';
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="api-staging-overlay">
        <div className="api-staging-modal">
          <div className="api-staging-loading">
            <div className="api-staging-loading-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </div>
            <h3>Loading Staged Imports</h3>
            <p>Fetching your queued contacts...</p>
            <div className="api-staging-skeleton-rows">
              <div className="api-staging-skeleton-row">
                <div className="api-staging-skeleton-pill" />
                <div className="api-staging-skeleton-bar" />
                <div className="api-staging-skeleton-bar short" />
              </div>
              <div className="api-staging-skeleton-row">
                <div className="api-staging-skeleton-pill" />
                <div className="api-staging-skeleton-bar" />
                <div className="api-staging-skeleton-bar short" />
              </div>
              <div className="api-staging-skeleton-row">
                <div className="api-staging-skeleton-pill" />
                <div className="api-staging-skeleton-bar" />
                <div className="api-staging-skeleton-bar short" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="api-staging-overlay">
      <div className="api-staging-modal">
        <button className="api-staging-close" onClick={onClose}>×</button>
        
        <div className="api-staging-header">
          <div className="api-staging-header-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="api-staging-header-text">
            <h2>API Import Review</h2>
            <p>Review and import contacts received via API</p>
          </div>
        </div>

        {error && (
          <div className="api-staging-error">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 8V12M12 16H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            {error}
          </div>
        )}

        <div className="api-staging-content">
          {/* Batch List */}
          <div className="api-staging-list">
            <h3>Pending Imports ({batches.length})</h3>
            
            {batches.length === 0 ? (
              <div className="api-staging-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                </svg>
                <p>No pending imports</p>
                <span>New contacts sent via API will appear here</span>
              </div>
            ) : (
              <div className="api-staging-batch-list">
                {batches.map(batch => (
                  <div 
                    key={batch.id}
                    className={`api-staging-batch-item ${selectedBatch?.id === batch.id ? 'selected' : ''}`}
                    onClick={() => selectBatch(batch)}
                  >
                    <div className="api-staging-batch-info">
                      <span className="api-staging-batch-name">{batch.importName}</span>
                      <span className="api-staging-batch-date">{formatDate(batch.createdAt)}</span>
                    </div>
                    <div className="api-staging-batch-count">
                      {formatNumber(batch.contactCount)} contacts
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Contact Preview */}
          <div className="api-staging-preview">
            {selectedBatch ? (
              <>
                <div className="api-staging-preview-header">
                  <div className="api-staging-import-name-section">
                    <label className="api-staging-import-name-label">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                        <path d="M19 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M7 7H17M7 12H17M7 17H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                      Import Name
                    </label>
                    <div className="api-staging-import-name-wrapper">
                      <input 
                        type="text"
                        className="api-staging-import-name-input"
                        value={customImportName}
                        onChange={(e) => {
                          setCustomImportName(e.target.value);
                          setShowImportNameDropdown(true);
                        }}
                        onFocus={() => setShowImportNameDropdown(true)}
                        placeholder="Enter or select import name..."
                      />
                      {existingImportNames.length > 0 && (
                        <button 
                          className="api-staging-import-name-toggle"
                          onClick={() => setShowImportNameDropdown(!showImportNameDropdown)}
                          type="button"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </button>
                      )}
                      {showImportNameDropdown && filteredImportNames.length > 0 && (
                        <div className="api-staging-import-name-dropdown">
                          <div className="api-staging-dropdown-header">Existing Lists</div>
                          {filteredImportNames.map((name, idx) => (
                            <div 
                              key={idx}
                              className="api-staging-dropdown-item"
                              onClick={() => selectImportName(name)}
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                <path d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              {name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <span className="api-staging-import-name-hint">
                      Select existing list or create new one
                    </span>
                  </div>
                  <span className="api-staging-contact-count">{formatNumber(selectedBatch.contactCount)} contacts</span>
                </div>

                {/* Field Mapping Section */}
                {availableSourceFields.length > 0 && (
                  <div className="api-staging-mapping-section">
                    <div className="api-staging-mapping-header-static">
                      <div className="api-staging-mapping-header-left">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                          <path d="M16 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V8L16 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M16 3V8H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>Column Mapping</span>
                      </div>
                      <span className="api-staging-mapping-count-badge">
                        {Object.keys(fieldMapping).filter(k => {
                          const v = fieldMapping[k];
                          return Array.isArray(v) ? v.length > 0 : !!v;
                        }).length} mapped
                      </span>
                    </div>
                    
                    <div className="api-staging-mapping-container">
                        {availableSourceFields.map((sourceField) => {
                          const mappedTo = Object.entries(fieldMapping).find(([_, v]) => {
                            const sources = Array.isArray(v) ? v : [v];
                            return sources.includes(sourceField);
                          })?.[0];
                          const sampleValue = contacts[0]?.[sourceField];
                          const siblingCount = mappedTo
                            ? (Array.isArray(fieldMapping[mappedTo]) ? fieldMapping[mappedTo].length : 1) - 1
                            : 0;
                          const isNumericTarget = mappedTo && NUMERIC_FIELDS.has(mappedTo);
                          const hasNonNumericData = isNumericTarget && sampleValue && isNaN(parseFloat(sampleValue));
                          
                          return (
                            <div key={sourceField} className={`api-staging-mapping-row ${mappedTo ? 'mapped' : ''} ${hasNonNumericData ? 'has-warning' : ''}`}>
                              <div className="api-staging-mapping-source-col">
                                <span className="api-staging-mapping-source-name">{sourceField}</span>
                                {sampleValue && (
                                  <span className="api-staging-mapping-sample">
                                    {String(sampleValue).substring(0, 25)}{String(sampleValue).length > 25 ? '...' : ''}
                                  </span>
                                )}
                                {hasNonNumericData && (
                                  <span className="api-staging-mapping-type-warning">
                                    Numbers only — text values will become 0
                                  </span>
                                )}
                              </div>
                              <div className="api-staging-mapping-target-col">
                                <select
                                  className="api-staging-mapping-select"
                                  value={mappedTo || ''}
                                  onChange={(e) => {
                                    const newTarget = e.target.value;
                                    setFieldMapping(prev => {
                                      const updated = { ...prev };
                                      for (const [key, val] of Object.entries(updated)) {
                                        if (Array.isArray(val)) {
                                          updated[key] = val.filter(v => v !== sourceField);
                                          if (updated[key].length === 0) delete updated[key];
                                          else if (updated[key].length === 1) updated[key] = updated[key][0];
                                        } else if (val === sourceField) {
                                          delete updated[key];
                                        }
                                      }
                                      if (newTarget) {
                                        const existing = updated[newTarget];
                                        if (Array.isArray(existing)) {
                                          updated[newTarget] = [...existing, sourceField];
                                        } else if (existing) {
                                          updated[newTarget] = [existing, sourceField];
                                        } else {
                                          updated[newTarget] = sourceField;
                                        }
                                      }
                                      return updated;
                                    });
                                  }}
                                >
                                  <option value="">Select...</option>
                                  {targetFields.map(({ key, label }) => {
                                    const targetSources = fieldMapping[key];
                                    const count = targetSources
                                      ? (Array.isArray(targetSources) ? targetSources.length : 1)
                                      : 0;
                                    const isSelf = mappedTo === key;
                                    const numHint = NUMERIC_FIELDS.has(key) ? ' (#)' : '';
                                    return (
                                      <option key={key} value={key}>
                                        {label}{numHint}{count > 0 && !isSelf ? ` (+${count})` : ''}
                                      </option>
                                    );
                                  })}
                                </select>
                                {siblingCount > 0 && (
                                  <span className="api-staging-mapping-multi-badge">
                                    +{siblingCount} merged
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}

                        {selectedBatch?.apiKeyId && (
                          <div className="api-staging-remember-mapping">
                            {hasSavedMapping && (
                              <span className="api-staging-remember-hint">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                  <path d="M20 6L9 17L4 12" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                                Mapping loaded from last import
                              </span>
                            )}
                            <label className="api-staging-auto-import-label" onClick={() => setAutoImportEnabled(!autoImportEnabled)}>
                              <div className={`api-staging-auto-import-toggle ${autoImportEnabled ? 'active' : ''}`}>
                                <div className="api-staging-auto-import-dot" />
                              </div>
                              <span>Auto-Import</span>
                              <span className="api-staging-auto-import-hint">
                                {autoImportEnabled ? 'Future batches from this API key will import automatically' : 'Enable to skip manual review for this API key'}
                              </span>
                            </label>
                          </div>
                        )}
                      </div>
                  </div>
                )}

                {loadingContacts ? (
                  <div className="api-staging-preview-loading">
                    <div className="api-staging-spinner"></div>
                    <p>Loading contacts...</p>
                  </div>
                ) : (
                  <>
                    <div className="api-staging-actions">
                      <button 
                        className="api-staging-delete-btn"
                        onClick={handleDelete}
                        disabled={deleting || importing}
                      >
                        {deleting ? 'Deleting...' : 'Delete'}
                      </button>
                      <button 
                        className="api-staging-import-btn"
                        onClick={handleImport}
                        disabled={importing || deleting}
                      >
                        {importing ? (
                          <>
                            <span className="api-staging-btn-spinner"></span>
                            Importing...
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                              <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                            Import {formatNumber(selectedBatch.contactCount)} Contacts
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="api-staging-preview-empty">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>Select a batch to preview</p>
                <span>Click on a pending import to view its contacts</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiStagingReview;
